import { dashboard, login, register } from '@/routes';
import { type SharedData } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowUpRight, Check, Play, Menu, X, Mail, LogIn, ArrowRight,
  PhoneCall, Hash, Activity, Users, CalendarClock, ShieldCheck,
  Coins, Zap, Gauge, Infinity as InfinityIcon,
} from 'lucide-react';

// ── Types ──
interface ThemeSettings {
  site_name: string;
  site_tagline: string;
  primary_color: string;
  secondary_color: string;
  can_register: boolean;
  copyright_text: string;
}
interface ThemeStat { number: string; label: string; icon: string; }
interface ThemeFeature { icon: string; title: string; description: string; gradient: string; icon_bg: string; }
interface ThemeBenefit { icon: string; title: string; description: string; }
interface ThemeUseCase { icon: string; title: string; description: string; items: string[]; color: string; }
interface ThemePricing { credits: string; price: string; per_credit: string; popular: boolean; icon: string; savings?: string; }
interface ThemeFaq { question: string; answer: string; icon: string; }
interface ThemeHero { badge_text: string; typewriter_text: string; subtitle: string; primary_cta_text: string; secondary_cta_text: string; }
interface ThemeFooter { cta_badge_text: string; cta_headline: string; cta_description: string; cta_primary_text: string; cta_secondary_text: string; }

interface WelcomeProps {
  canRegister?: boolean;
  settings: ThemeSettings;
  hero: ThemeHero;
  stats: ThemeStat[];
  benefits: ThemeBenefit[];
  footer: ThemeFooter;
  features?: ThemeFeature[];
  useCases?: ThemeUseCase[];
  pricing?: ThemePricing[];
  faqs?: ThemeFaq[];
}

// ── Data fallbacks ──
const NAV_LINKS = [
  { id: 'why', label: 'Why' },
  { id: 'features', label: 'Features' },
  { id: 'use-cases', label: 'Use Cases' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'faq', label: 'FAQ' },
];

const SUPPORT_POINTS = ['Instant activation', '40+ languages', '<100ms latency', '99.9% uptime'];

const VALUE_CARDS = [
  { icon: 'Coins', title: 'Pay As You Go', body: 'Only buy the credits you need. No monthly subscription required.', id: 'pay' },
  { icon: 'Zap', title: 'Instant Activation', body: 'Start launching campaigns as soon as your credits are available.', id: 'instant' },
  { icon: 'Gauge', title: 'Flexible for Any Volume', body: 'Run small tests or large campaigns without changing platforms.', id: 'flex' },
  { icon: 'Infinity', title: 'No Credit Expiration', body: 'Your credits stay available until you actually use them.', id: 'no-exp' },
];

const BRIDGE_POINTS = ['Launch faster', 'Reach more contacts', 'Reduce staff workload', 'Scale only when needed'];

const FALLBACK_STATS = [
  { number: '10M+', label: 'Calls Launched' },
  { number: '99.9%', label: 'Uptime SLA' },
  { number: '40+', label: 'Languages' },
  { number: '<100ms', label: 'Response Latency' },
];

const FALLBACK_FEATURES = [
  { icon: 'PhoneCall', title: 'AI Voice Campaigns', description: 'Launch voice campaigns that scale outreach automatically.' },
  { icon: 'Hash', title: 'Interactive DTMF', description: 'Let recipients respond using keypad inputs for routing and confirmations.' },
  { icon: 'Activity', title: 'Real-Time Analytics', description: 'Monitor campaign performance live with full visibility.' },
  { icon: 'Users', title: 'Contact Management', description: 'Import, organize, and manage contact lists for repeatable campaigns.' },
  { icon: 'CalendarClock', title: 'Smart Scheduling', description: 'Control when campaigns go out to reach people at the right time.' },
  { icon: 'ShieldCheck', title: 'Enterprise Security', description: 'Run campaigns with reliability, access control, and safeguards.' },
];

const FALLBACK_USE_CASES = [
  { title: 'Lead Qualification', description: 'Pre-qualify prospects automatically before your sales team follows up.', items: ['Ask qualification questions automatically', 'Route high-intent leads faster', 'Reduce manual screening time', 'Improve sales efficiency'] },
  { title: 'Voice Broadcasts', description: 'Send announcements, reminders, and campaign messages at scale.', items: ['Broadcast important updates', 'Reach large contact lists quickly', 'Standardize messaging', 'Launch in minutes'] },
  { title: 'Surveys & Feedback', description: 'Collect customer responses and feedback through automated voice interactions.', items: ['Gather feedback automatically', 'Run post-service surveys', 'Improve response rates', 'Turn calls into useful data'] },
];

const FALLBACK_FAQS = [
  { question: 'How do voice credits work?', answer: 'Voice credits are used when you run campaigns. You only pay for the credits you purchase.' },
  { question: 'Do credits expire?', answer: 'No. Your credits remain available until you use them.' },
  { question: 'Can I start with a small campaign?', answer: 'Yes. RappiCalls works whether you want to test a small campaign or run larger outreach.' },
  { question: 'What can I use RappiCalls for?', answer: 'Lead qualification, voice broadcasts, reminders, surveys, customer feedback, and other outbound calling workflows.' },
  { question: 'What languages are supported?', answer: 'RappiCalls supports multilingual voice interactions so you can reach more audiences.' },
  { question: 'Do I need a subscription?', answer: 'No. RappiCalls uses a pay-as-you-go model.' },
];

// ── Hooks ──
function useReveal() {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('rc-in'); io.unobserve(entry.target); }
      }),
      { threshold: 0.12 }
    );
    el.querySelectorAll('.rc-reveal').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
}

// ── Icon map ──
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  PhoneCall, Hash, Activity, Users, CalendarClock, ShieldCheck,
  Coins, Zap, Gauge, Infinity: InfinityIcon,
};
const getIcon = (name: string) => ICON_MAP[name] || PhoneCall;

// ── WaveformViz ──
function WaveformViz() {
  const bars = Array.from({ length: 48 });
  return (
    <div aria-hidden className="flex items-end gap-[3px] h-full w-full">
      {bars.map((_, i) => (
        <span key={i} className="rc-bar block w-[6px] bg-gradient-to-t from-[#FF3800] via-[#FF3800]/70 to-white/80"
          style={{ height: `${10 + ((i * 37) % 90)}%`, animationDelay: `${(i % 12) * 0.08}s` }} />
      ))}
    </div>
  );
}

// ── SiteHeader ──
function SiteHeader({ auth, canRegister }: { auth: any; canRegister: boolean }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled ? 'bg-[#050505]/85 backdrop-blur-xl border-b border-[#262626]' : 'bg-transparent'}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" className="font-mono text-sm md:text-base font-bold tracking-[0.02em] text-white flex items-center gap-2">
          <PhoneCall className="h-7 w-7 text-[#FF3800]" />
          RAPPI<span className="text-[#FF3800]">calls</span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button key={l.id} onClick={() => scrollTo(l.id)}
              className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-white transition-colors">{l.label}</button>
          ))}
          <a href="/carrier" className="text-xs uppercase tracking-[0.2em] text-[#FF3800] hover:text-white transition-colors font-bold">For Carriers</a>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {auth.user ? (
            <Link href={dashboard()} className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-white px-3 py-2">Dashboard</Link>
          ) : (
            <>
              <Link href={login()} className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-white px-3 py-2">Sign in</Link>
              {canRegister && (
                <Link href={register()} className="group bg-[#FF3800] hover:bg-[#E63200] text-white px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-2 transition-colors">
                  Get Started <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              )}
            </>
          )}
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#050505] border-t border-[#262626] px-6 py-6">
          <div className="flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)}
                className="text-left text-sm uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-white">{l.label}</button>
            ))}
            {auth.user ? (
              <Link href={dashboard()} className="mt-2 bg-[#FF3800] text-white px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold text-center">Dashboard →</Link>
            ) : (
              <>
                <Link href={login()} className="mt-2 border border-[#FF3800] text-[#FF3800] px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold text-center">Sign In</Link>
                {canRegister && (
                  <Link href={register()} className="bg-[#FF3800] text-white px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold text-center">Get Started →</Link>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

// ── HeroSection ──
function HeroSection({ auth, canRegister }: { auth: any; canRegister: boolean }) {
  return (
    <section id="top" className="relative pt-28 md:pt-40 pb-16 md:pb-24 overflow-hidden">
      <div className="absolute inset-0 rc-grid-bg opacity-60 pointer-events-none" />
      <div className="absolute -top-40 right-[-10%] w-[600px] h-[600px] rounded-full bg-[#FF3800]/10 blur-[120px] pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-end">
        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] text-[#FF3800] border border-[#FF3800]/40 px-3 py-1.5 mb-8">
            <span className="w-1.5 h-1.5 bg-[#FF3800] rc-pulse" />AI Voice Campaigns / Modern Businesses
          </div>
          <h1 className="rc-h1 text-white text-[40px] sm:text-5xl md:text-6xl lg:text-[68px] xl:text-[80px]">
            Stop wasting time on{' '}<span className="text-[#FF3800]">manual calling.</span><br />
            <span className="text-white/70">Launch AI voice campaigns that</span>{' '}
            <em className="not-italic font-display font-semibold underline decoration-[#FF3800] decoration-[4px] underline-offset-[6px]">scale.</em>
          </h1>
          <p className="mt-8 text-[#A3A3A3] text-base md:text-lg max-w-2xl leading-relaxed">
            Qualify leads, send voice broadcasts, automate reminders, and collect feedback with a{' '}
            <span className="text-white">pay-as-you-go</span> calling system built for business results.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            {auth.user ? (
              <Link href={dashboard()} className="group bg-[#FF3800] hover:bg-[#E63200] text-white px-7 py-4 text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-3 transition-colors">
                Go to Dashboard <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            ) : (
              <>
                {canRegister && (
                  <Link href={register()} className="group bg-[#FF3800] hover:bg-[#E63200] text-white px-7 py-4 text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-3 transition-colors">
                    Start Free Trial <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                )}
                <Link href={login()} className="group border border-[#FF3800] text-[#FF3800] hover:bg-[#FF3800]/10 px-7 py-4 text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-3 transition-colors">
                  <Play className="w-4 h-4" /> Sign In
                </Link>
              </>
            )}
          </div>
          <div className="mt-6 font-mono text-xs text-[#737373] uppercase tracking-[0.2em]">No subscriptions · No expiration · No wasted spend</div>
          <ul className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
            {SUPPORT_POINTS.map((p, i) => (
              <li key={p} className="flex items-center gap-2 text-xs md:text-sm text-[#A3A3A3]">
                <Check className="w-4 h-4 text-[#FF3800] shrink-0" /><span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-4">
          <div className="relative border border-[#262626] bg-[#0A0A0A] p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">
                <span className="w-2 h-2 bg-[#00FF66] rc-pulse" />Live · outbound_campaign.001
              </div>
              <div className="font-mono text-[10px] text-[#737373]">[REC]</div>
            </div>
            <div className="h-40 md:h-56 relative"><WaveformViz /></div>
            <div className="mt-6 grid grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
              {[{ k: 'Calls/min', v: '1,204' }, { k: 'Connected', v: '87.3%' }, { k: 'Avg Dur', v: '00:42' }].map((m) => (
                <div key={m.k} className="bg-[#0A0A0A] p-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#737373]">{m.k}</div>
                  <div className="font-mono text-lg md:text-xl text-white mt-1">{m.v}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] flex items-center justify-between">
              <span>Region: US-E1</span><span className="text-[#FF3800]">Latency ~82ms</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── StatsSection ──
function StatsSection({ stats }: { stats: ThemeStat[] }) {
  const ref = useReveal();
  const items = stats?.length ? stats : FALLBACK_STATS;
  return (
    <section ref={ref} className="border-y border-[#262626] bg-[#050505] relative">
      <div className="max-w-[1400px] mx-auto grid grid-cols-2 lg:grid-cols-4">
        {items.map((s, i) => (
          <div key={i} className={`rc-reveal p-8 md:p-12 border-[#262626] ${i < 3 ? 'lg:border-r' : ''} ${i % 2 === 0 ? 'border-r lg:border-r' : ''} ${i < 2 ? 'border-b lg:border-b-0' : ''}`}
            style={{ transitionDelay: `${i * 80}ms` }}>
            <div className="font-mono text-4xl md:text-6xl font-bold text-white tracking-tight">{s.number}</div>
            <div className="mt-3 font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF3800]">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── ValuePropSection ──
function ValuePropSection() {
  const ref = useReveal();
  return (
    <section ref={ref} id="why" className="relative py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-5 rc-reveal">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3800] mb-4 flex items-center gap-2">
              <span className="block w-8 h-px bg-[#FF3800]" />Why RappiCalls
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white tracking-tight leading-[0.95]">
              Built to launch campaigns <span className="text-[#FF3800]">fast</span> — without subscriptions or complexity.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 rc-reveal">
            <p className="text-[#A3A3A3] text-base md:text-lg leading-relaxed">
              RappiCalls gives your team a faster way to run voice outreach without building a call center, hiring agents, or committing to monthly software contracts.
            </p>
            <div className="mt-8 font-mono text-xs text-[#737373] uppercase tracking-[0.2em]">→ Operational efficiency. Not operational overhead.</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
          {VALUE_CARDS.map((card, i) => {
            const Icon = getIcon(card.icon);
            return (
              <div key={card.id} className="rc-reveal group relative bg-[#0A0A0A] p-8 md:p-12 hover:bg-[#111111] transition-colors min-h-[260px] flex flex-col justify-between"
                style={{ transitionDelay: `${i * 100}ms` }}>
                <div>
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 flex items-center justify-center border border-[#262626] bg-[#111111] text-[#FF3800] group-hover:border-[#FF3800] transition-colors">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373]">{String(i + 1).padStart(2, '0')} / {String(VALUE_CARDS.length).padStart(2, '0')}</div>
                  </div>
                  <h3 className="mt-8 font-display text-2xl md:text-3xl text-white tracking-tight">{card.title}</h3>
                  <p className="mt-4 text-[#A3A3A3] leading-relaxed">{card.body}</p>
                </div>
                <div className="mt-8 flex items-center gap-2 text-[#FF3800] opacity-0 group-hover:opacity-100 transition-opacity font-mono text-[10px] uppercase tracking-[0.25em]">
                  Learn more <ArrowUpRight className="w-3 h-3" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── FeaturesSection ──
function FeaturesSection({ features }: { features: ThemeFeature[] }) {
  const ref = useReveal();
  const items = features?.length ? features : FALLBACK_FEATURES;
  return (
    <section ref={ref} id="features" className="relative border-t border-[#262626] py-24 md:py-36 bg-[#080808]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-16 md:mb-20 max-w-3xl rc-reveal">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3800] mb-4 flex items-center gap-2">
            <span className="block w-8 h-px bg-[#FF3800]" />Features
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white tracking-tight leading-[0.95]">Everything you need to run AI voice campaigns.</h2>
          <p className="mt-6 text-[#A3A3A3] text-base md:text-lg leading-relaxed">Built for speed, designed for simplicity, and ready for real business outreach from day one.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
          {items.map((f, i) => {
            const Icon = getIcon(f.icon);
            return (
              <article key={i} className="rc-reveal group relative bg-[#0A0A0A] hover:bg-[#111111] transition-colors p-8 md:p-10 min-h-[280px]"
                style={{ transitionDelay: `${(i % 3) * 80}ms` }}>
                <div className="flex items-center justify-between mb-8">
                  <div className="w-12 h-12 flex items-center justify-center border border-[#262626] text-[#FF3800] group-hover:border-[#FF3800] group-hover:bg-[#FF3800]/5 transition-colors">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373]">F.{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="font-display text-2xl text-white tracking-tight">{f.title}</h3>
                <p className="mt-4 text-[#A3A3A3] leading-relaxed text-sm md:text-base">{f.description}</p>
                <span className="absolute bottom-0 left-0 h-[2px] bg-[#FF3800] w-0 group-hover:w-full transition-all duration-500" />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── UseCasesSection ──
function UseCasesSection({ useCases }: { useCases: ThemeUseCase[] }) {
  const ref = useReveal();
  const items = useCases?.length ? useCases : FALLBACK_USE_CASES;
  return (
    <section ref={ref} id="use-cases" className="relative py-24 md:py-36 border-t border-[#262626]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-7 rc-reveal">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3800] mb-4 flex items-center gap-2">
              <span className="block w-8 h-px bg-[#FF3800]" />Use Cases
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white tracking-tight leading-[0.95]">Built for real business outreach.</h2>
          </div>
          <div className="lg:col-span-5 rc-reveal flex items-end">
            <p className="text-[#A3A3A3] text-base md:text-lg leading-relaxed">From lead generation to customer surveys, RappiCalls adapts to the way your business communicates.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
          {items.map((uc, i) => (
            <div key={i} className="rc-reveal group bg-[#0A0A0A] hover:bg-[#111111] p-8 md:p-10 min-h-[420px] flex flex-col transition-colors"
              style={{ transitionDelay: `${i * 120}ms` }}>
              <div className="flex items-center justify-between mb-8">
                <span className="font-mono text-sm text-[#FF3800]">{String(i + 1).padStart(2, '0')}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373]">Workflow</span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl text-white tracking-tight leading-[1.0]">{uc.title}</h3>
              <p className="mt-4 text-[#A3A3A3] leading-relaxed">{uc.description}</p>
              <ul className="mt-8 space-y-3 border-t border-[#262626] pt-6">
                {uc.items.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-[#D4D4D4]">
                    <span className="mt-0.5 w-5 h-5 flex items-center justify-center border border-[#FF3800]/40 text-[#FF3800] shrink-0">
                      <Check size={12} strokeWidth={2} />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── CommercialBridgeSection ──
function CommercialBridgeSection() {
  const ref = useReveal();
  return (
    <section ref={ref} className="relative py-24 md:py-36 border-t border-[#262626] overflow-hidden">
      <div className="absolute inset-0 rc-grid-bg opacity-30 pointer-events-none" />
      <div className="absolute -left-32 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#FF3800]/8 blur-[140px] rounded-full" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 rc-reveal">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3800] mb-4 flex items-center gap-2">
            <span className="block w-8 h-px bg-[#FF3800]" />The Shift
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-[64px] text-white tracking-tight leading-[0.95]">
            Replace manual calling with a <span className="text-[#FF3800]">faster, more scalable</span> system.
          </h2>
          <p className="mt-8 text-[#A3A3A3] text-base md:text-lg leading-relaxed max-w-xl">
            Manual outbound calling is slow, expensive, and difficult to scale. RappiCalls gives your business an easier way to launch voice outreach and keep campaigns moving without increasing payroll.
          </p>
        </div>
        <div className="lg:col-span-5 rc-reveal">
          <ul className="border border-[#262626] divide-y divide-[#262626] bg-[#0A0A0A]">
            {BRIDGE_POINTS.map((p, i) => (
              <li key={p} className="group flex items-center justify-between p-6 md:p-7 hover:bg-[#111111] transition-colors">
                <div className="flex items-center gap-5">
                  <span className="font-mono text-xs text-[#737373] w-8">0{i + 1}</span>
                  <span className="font-display text-xl md:text-2xl text-white tracking-tight">{p}</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#FF3800] opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ── PricingSection ──
function PricingSection({ auth, canRegister }: { auth: any; canRegister: boolean }) {
  const ref = useReveal();
  return (
    <section ref={ref} id="pricing" className="relative py-24 md:py-36 border-t border-[#262626] bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="text-center mb-12 md:mb-16 rc-reveal">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3800] mb-4">Credits & Pricing</div>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl text-white tracking-tight leading-[0.95] max-w-5xl mx-auto">
            Buy Credits. Launch Calls. <span className="text-[#FF3800]">Scale When Ready.</span>
          </h2>
          <p className="mt-8 text-[#A3A3A3] text-base md:text-lg max-w-2xl mx-auto">
            Pay-as-you-go. No subscriptions. No commitments. Credits never expire. Start small, test campaigns, and scale when results justify it.
          </p>
        </div>
        <div className="relative rc-reveal max-w-4xl mx-auto border border-[#262626] bg-[#0A0A0A] p-10 md:p-16 overflow-hidden">
          <div className="absolute inset-0 rc-grid-bg opacity-40 pointer-events-none" />
          <div className="absolute -right-32 -top-32 w-80 h-80 bg-[#FF3800]/12 blur-[100px] rounded-full pointer-events-none" />
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
            <div className="md:col-span-2">
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#FF3800] mb-5 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#FF3800] rc-pulse" />Flexible Pricing
              </div>
              <h3 className="font-display text-3xl md:text-4xl text-white tracking-tight leading-[1.0]">
                Simple pricing for teams that want flexibility without lock-in.
              </h3>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
                {[{ k: 'Model', v: 'Pay as you go' }, { k: 'Commitment', v: 'None' }, { k: 'Expiration', v: 'Never' }].map((x) => (
                  <li key={x.k} className="bg-[#0A0A0A] p-5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373]">{x.k}</div>
                    <div className="font-display text-lg text-white mt-2">{x.v}</div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-3 md:pt-2">
              {auth.user ? (
                <Link href={dashboard()} className="group w-full bg-[#FF3800] hover:bg-[#E63200] text-white px-6 py-5 text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-3 transition-colors">
                  Go to Dashboard <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              ) : (
                <>
                  {canRegister && (
                    <Link href={register()} className="group w-full bg-[#FF3800] hover:bg-[#E63200] text-white px-6 py-5 text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-3 transition-colors">
                      Get Started <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  )}
                  <Link href={login()} className="w-full border border-[#FF3800] text-[#FF3800] hover:bg-[#FF3800]/10 px-6 py-5 text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-3 transition-colors">
                    <Mail className="w-4 h-4" /> Sign In
                  </Link>
                </>
              )}
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[#737373] text-center">Most popular · Teams & Agencies</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── FAQSection ──
function FAQSection({ faqs }: { faqs: ThemeFaq[] }) {
  const ref = useReveal();
  const items = faqs?.length ? faqs : FALLBACK_FAQS;
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <section ref={ref} id="faq" className="relative py-24 md:py-36 border-t border-[#262626]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 rc-reveal">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3800] mb-4 flex items-center gap-2">
            <span className="block w-8 h-px bg-[#FF3800]" />FAQ
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white tracking-tight leading-[0.95]">Got questions?</h2>
          <p className="mt-6 text-[#A3A3A3] text-base md:text-lg leading-relaxed max-w-md">Here are answers to the most common questions about getting started with RappiCalls.</p>
          <div className="mt-10 font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
            Still unsure? <a href="#pricing" className="text-[#FF3800] hover:underline">Contact our team →</a>
          </div>
        </div>
        <div className="lg:col-span-7 rc-reveal">
          <div className="w-full">
            {items.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <div key={i} className="border-b border-[#262626]">
                  <button onClick={() => setOpenIndex(isOpen ? null : i)}
                    className={`w-full text-left py-6 font-display text-lg md:text-xl tracking-tight flex items-start gap-4 transition-colors ${isOpen ? 'text-[#FF3800]' : 'text-white hover:text-[#FF3800]'}`}>
                    <span className="font-mono text-[11px] text-[#737373] mt-1.5">0{i + 1}</span>
                    <span className="flex-1">{item.question}</span>
                    <span className={`text-[#737373] transition-transform ${isOpen ? 'rotate-45' : ''}`}>+</span>
                  </button>
                  {isOpen && (
                    <div className="text-[#A3A3A3] leading-relaxed pb-6 pl-10 text-base">{item.answer}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── FinalCTASection ──
function FinalCTASection({ auth, canRegister }: { auth: any; canRegister: boolean }) {
  const ref = useReveal();
  return (
    <section ref={ref} className="relative py-28 md:py-44 border-t border-[#262626] overflow-hidden bg-[#050505]">
      <div className="absolute inset-0 rc-grid-bg opacity-40 pointer-events-none" />
      <div className="absolute left-1/2 -translate-x-1/2 -top-40 w-[900px] h-[500px] bg-[#FF3800]/10 blur-[160px] rounded-full pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 text-center">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3800] mb-6">Start Today</div>
        <h2 className="font-display text-4xl md:text-6xl lg:text-[96px] text-white tracking-tight leading-[0.92] max-w-5xl mx-auto rc-reveal">
          Ready to launch your next <span className="text-[#FF3800]">voice campaign?</span>
        </h2>
        <p className="mt-8 text-[#A3A3A3] text-base md:text-lg max-w-2xl mx-auto rc-reveal">
          Start making calls today with a flexible pay-as-you-go system built for modern business outreach.
        </p>
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3 rc-reveal">
          {auth.user ? (
            <Link href={dashboard()} className="group bg-[#FF3800] hover:bg-[#E63200] text-white px-8 py-5 text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-3 transition-colors w-full sm:w-auto">
              Go to Dashboard <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          ) : (
            <>
              {canRegister && (
                <Link href={register()} className="group bg-[#FF3800] hover:bg-[#E63200] text-white px-8 py-5 text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-3 transition-colors w-full sm:w-auto">
                  Get Started <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              )}
              <Link href={login()} className="group border border-[#FF3800] text-[#FF3800] hover:bg-[#FF3800]/10 px-8 py-5 text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-3 transition-colors w-full sm:w-auto">
                <LogIn className="w-4 h-4" /> Sign In
              </Link>
            </>
          )}
        </div>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-mono text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#737373] rc-reveal">
          <span>· No subscription required</span><span>· Credits never expire</span><span>· Built for business use</span>
        </div>
      </div>
    </section>
  );
}

// ── SiteFooter ──
function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-[#262626] bg-[#050505] py-16 md:py-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="font-mono text-base font-bold tracking-tight text-white flex items-center gap-2">
            <PhoneCall className="h-7 w-7 text-[#FF3800]" />RAPPI<span className="text-[#FF3800]">calls</span>
          </div>
          <p className="mt-5 font-display text-xl md:text-2xl text-white tracking-tight leading-tight max-w-md">AI-powered voice calling for modern business outreach.</p>
          <p className="mt-4 text-[#A3A3A3] text-sm leading-relaxed max-w-md">Launch campaigns faster, automate repetitive calling tasks, and scale outreach without the burden of manual follow-up.</p>
        </div>
        <div className="md:col-span-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-5">Product</div>
          <ul className="space-y-3 text-sm">
            {['Features', 'Use Cases', 'Pricing', 'FAQ'].map((l) => (
              <li key={l}><a href={`#${l.toLowerCase().replace(' ', '-')}`} className="text-[#D4D4D4] hover:text-[#FF3800] transition-colors">{l}</a></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-5">Company</div>
          <ul className="space-y-3 text-sm">
            {['About', 'Contact', 'Careers'].map((l) => (
              <li key={l}><a href="#" className="text-[#D4D4D4] hover:text-[#FF3800] transition-colors">{l}</a></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-5">Legal</div>
          <ul className="space-y-3 text-sm">
            {[{t:'Privacy Policy',h:'#'},{t:'Terms of Service',h:'#'},{t:'For Carriers',h:'/carrier'}].map(({t,h}) => (
              <li key={t}><a href={h} className="text-[#D4D4D4] hover:text-[#FF3800] transition-colors">{t}</a></li>
            ))}
          </ul>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-16 pt-8 border-t border-[#262626] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#737373]">© {year} RappiCalls · All rights reserved</div>
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#737373] flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-[#00FF66] rc-pulse" />All systems operational
        </div>
      </div>
    </footer>
  );
}

// ── Main Component ──
export default function Welcome({
  canRegister = true,
  settings,
  hero,
  stats,
  benefits,
  footer,
  features = [],
  useCases = [],
  pricing = [],
  faqs = [],
}: WelcomeProps) {
  const { auth } = usePage<SharedData>().props;

  return (
    <>
      <Head title={settings?.site_name || 'RappiCalls'} />
      <div className="min-h-screen bg-[#050505] text-white antialiased selection:bg-[#FF3800] selection:text-[#050505]">
        <SiteHeader auth={auth} canRegister={canRegister} />
        <main>
          <HeroSection auth={auth} canRegister={canRegister} />
          <StatsSection stats={stats} />
          <ValuePropSection />
          <FeaturesSection features={features} />
          <UseCasesSection useCases={useCases} />
          <CommercialBridgeSection />
          <PricingSection auth={auth} canRegister={canRegister} />
          <FAQSection faqs={faqs} />
          <FinalCTASection auth={auth} canRegister={canRegister} />
        </main>
        <SiteFooter />
      </div>

      <style>{`
        @import url("https://api.fontshare.com/v2/css?f[]=clash-display@500,600,700&f[]=satoshi@300,400,500,700&display=swap");
        @import url("https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&display=swap");

        .font-display {
          font-family: "Clash Display", "Satoshi", sans-serif;
          letter-spacing: -0.02em;
        }
        .font-mono {
          font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        }
        .rc-h1 {
          font-family: "Clash Display", sans-serif;
          font-weight: 600;
          letter-spacing: -0.035em;
          line-height: 0.92;
        }
        .rc-grid-bg {
          background-image:
            linear-gradient(to right, rgba(255,255,255,0.035) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.035) 1px, transparent 1px);
          background-size: 64px 64px;
        }
        @keyframes rc-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.25); }
        }
        .rc-pulse { animation: rc-pulse 1.4s ease-in-out infinite; }
        @keyframes rc-bar {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
        .rc-bar { transform-origin: center; animation: rc-bar 1.1s ease-in-out infinite; }
        .rc-reveal {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 700ms cubic-bezier(0.22,1,0.36,1), transform 700ms cubic-bezier(0.22,1,0.36,1);
        }
        .rc-reveal.rc-in { opacity: 1; transform: translateY(0); }
      `}</style>
    </>
  );
}
